import React, { useRef, useState } from 'react';

import { Col } from '@opendoor/atoms/box';
import { Button } from '@opendoor/atoms/button';
import { Checkbox } from '@opendoor/atoms/checkbox';
import { Field } from '@opendoor/atoms/field';
import { Input } from '@opendoor/atoms/input';
import { Text } from '@opendoor/atoms/text';
import { Toasty, useAtomsToastManager } from '@opendoor/atoms/toast';
import { MARKETING_SMS_CONSENT } from '@opendoor/auth-fe/forms';
import { globalObservability } from '@opendoor/observability/slim';
import { FooterRoutes, LandingPageRoutes } from 'components/shared/constants';
import { isValidEmailAddress } from 'components/shared/emailValidation';
import { useSmallerThanLG } from 'components/shared/hooks/useMediaQuery';

import { FormLayout } from '../components/shared/FormLayout';

const Sentry = globalObservability.getSentryClient();
const ZAPIER_WEBHOOK_URL = 'https://hooks.zapier.com/hooks/catch/19123831/277bvhz/';
const MAX_RETRIES = 3;
const RETRY_DELAY = 500;

const INTEREST_OPTIONS = [
  { label: 'Cash offers for your clients', value: 'cash_offers' },
  { label: 'Receiving leads from Opendoor', value: 'leads' },
  { label: 'Toolkits and resources', value: 'toolkits' },
  { label: 'Key agent events', value: 'events' },
];

type FormData = {
  firstName: string;
  lastName: string;
  email: string | undefined;
  phoneNumber: string | undefined;
  brokerage: string;
  mlsId: string;
  interests: string[];
  agreeToMarketing: boolean;
  mlsName: string;
};

type ValidationState = 'valid' | 'invalid' | 'unknown';

// The Bricks `ValidatedTextInput` (format="telephoneNumber") gated on a valid
// phone; preserve that gate with a simple 10-digit check (Atoms has no built-in
// phone formatting/validation).
const isValidPhoneNumber = (value: string): boolean => value.replace(/\D/g, '').length === 10;

const validationStateFor = (
  value: string,
  isValid: (input: string) => boolean,
): ValidationState => {
  if (!value) {
    return 'unknown';
  }
  return isValid(value) ? 'valid' : 'invalid';
};

const KeyAgentSignUpForm: React.FC = () => {
  // `useAtomsToastManager()` returns a fresh object every render, so keep it in a
  // ref and read `.current` at call time — never reference `toasts` directly in an
  // effect/dependency array (that loops).
  const toasts = useAtomsToastManager();
  const toastsRef = useRef(toasts);
  toastsRef.current = toasts;
  const smallerThanLG = useSmallerThanLG();
  const inputSize = smallerThanLG ? 'base' : 'lg';

  const [formData, setFormData] = useState<FormData>({
    firstName: '',
    lastName: '',
    email: undefined,
    phoneNumber: undefined,
    brokerage: '',
    mlsId: '',
    interests: [],
    agreeToMarketing: false,
    mlsName: '',
  });

  const [fieldErrors, setFieldErrors] = useState<Record<string, boolean>>({});
  const [fieldValidation, setFieldValidation] = useState<Record<string, ValidationState>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const validateName = (name: string): boolean => {
    const cleanedName = name.trim().replace(/[^a-zA-Z\s]/g, '');
    return cleanedName.length >= 1;
  };

  const handleInputChange = (field: string, value: string | boolean | string[]) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
    if (fieldErrors[field]) {
      setFieldErrors((prev) => ({
        ...prev,
        [field]: false,
      }));
    }
  };

  const handleEmailChange = (value: string) => {
    handleInputChange('email', value);
    setFieldValidation((prev) => ({
      ...prev,
      email: validationStateFor(value, isValidEmailAddress),
    }));
  };

  const handlePhoneChange = (value: string) => {
    handleInputChange('phoneNumber', value);
    if (fieldErrors.phone) {
      setFieldErrors((prev) => ({ ...prev, phone: false }));
    }
    setFieldValidation((prev) => ({
      ...prev,
      phone: validationStateFor(value, isValidPhoneNumber),
    }));
  };

  const toggleInterest = (value: string, checked: boolean) => {
    const next = checked
      ? [...formData.interests, value]
      : formData.interests.filter((interest) => interest !== value);
    handleInputChange('interests', next);
  };

  const submitFormWithRetry = async (retryCount = 0): Promise<Response> => {
    try {
      const response = await fetch(ZAPIER_WEBHOOK_URL, {
        method: 'POST',
        body: JSON.stringify({
          ...formData,
          createdAt: new Date().toISOString(),
        }),
      });

      if (!response.ok) {
        const error = new Error('Failed to submit key agent sign upform');
        Sentry.withScope?.((scope) => {
          scope.setExtras({
            formData,
            responseStatus: response.status,
            responseStatusText: response.statusText,
          });
          Sentry.captureException?.(error);
        });

        if (retryCount < MAX_RETRIES) {
          await new Promise((resolve) => setTimeout(resolve, RETRY_DELAY));
          return submitFormWithRetry(retryCount + 1);
        }
        throw error;
      }

      return response;
    } catch (error) {
      if (retryCount < MAX_RETRIES) {
        await new Promise((resolve) => setTimeout(resolve, RETRY_DELAY));
        return submitFormWithRetry(retryCount + 1);
      }
      throw error;
    }
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    const newFieldErrors: Record<string, boolean> = {
      firstName: !formData.firstName || !validateName(formData.firstName),
      lastName: !formData.lastName || !validateName(formData.lastName),
      email: !formData.email || fieldValidation.email !== 'valid',
      phone: Boolean(formData.phoneNumber) && fieldValidation.phone !== 'valid',
    };
    setFieldErrors(newFieldErrors);

    // `newFieldErrors.fullName` is always undefined (the keys are firstName/
    // lastName/email/phone). This gate is preserved verbatim from the pre-Atoms
    // version to keep submit behavior unchanged — first/last-name errors still
    // surface inline on the fields; intentionally left as-is for the migration.
    const hasErrors = newFieldErrors.fullName || newFieldErrors.email || newFieldErrors.phone;
    if (hasErrors) {
      toastsRef.current.add({
        title: 'Please complete the required fields, full name and email address.',
        variant: 'error',
        timeout: 4000,
      });
      const el = document.querySelector<HTMLInputElement>('input[aria-invalid="true"]');
      if (el) {
        el.focus();
        el.scrollIntoView({ block: 'center' });
      }
      return;
    }

    setIsSubmitting(true);

    try {
      await submitFormWithRetry();
      toastsRef.current.add({
        title:
          'Thanks for submitting your information! We’ll be in touch with more info in the coming months',
        variant: 'success',
        timeout: 4000,
      });
      // Keep the button disabled through the 4s redirect so a successful submit
      // can't be fired twice (which would create a duplicate Zapier lead).
      setTimeout(() => {
        window.location.assign(LandingPageRoutes.AGENTS);
      }, 4000);
    } catch (error) {
      toastsRef.current.add({
        title: 'Sorry, there was an error submitting your form. Please try again.',
        variant: 'error',
        timeout: 4000,
      });
      setIsSubmitting(false);
    }
  };

  return (
    <FormLayout formId="key-agent-signup-form" onSubmit={handleSubmit}>
      <FormLayout.ScrollContainer>
        <FormLayout.TopNav
          navLabel="Opendoor Key Agent™"
          textButtonProps={{
            label: 'Exit',
            href: LandingPageRoutes.AGENTS,
            analyticsName: 'key-agent-signup-exit',
          }}
        />
        <FormLayout.FormContent>
          <FormLayout.Title
            title="Sign up to be an Opendoor Key Agent™"
            subhead="Enter your details to stay connected with us and access free Silver Tier membership"
          />
          <FormLayout.QuestionContainer>
            <Field
              label="First name"
              id="firstName"
              error={fieldErrors.firstName ? 'Please enter your first name to continue' : undefined}
            >
              <Input
                id="firstName"
                inputMode="text"
                size={inputSize}
                analyticsName="key-agent-signup-first-name"
                value={formData.firstName}
                error={Boolean(fieldErrors.firstName)}
                aria-invalid={fieldErrors.firstName || undefined}
                onChange={(event) => handleInputChange('firstName', event.currentTarget.value)}
              />
            </Field>
            <Field
              label="Last name"
              id="lastName"
              error={fieldErrors.lastName ? 'Please enter your last name to continue' : undefined}
            >
              <Input
                id="lastName"
                inputMode="text"
                size={inputSize}
                analyticsName="key-agent-signup-last-name"
                value={formData.lastName}
                error={Boolean(fieldErrors.lastName)}
                aria-invalid={fieldErrors.lastName || undefined}
                onChange={(event) => handleInputChange('lastName', event.currentTarget.value)}
              />
            </Field>
            <Field
              label="Email address"
              id="email"
              error={
                fieldErrors.email
                  ? 'Please enter a valid email address to sign up, should include an @ and domain (ex. .com).'
                  : undefined
              }
            >
              <Input
                id="email"
                type="email"
                size={inputSize}
                analyticsName="key-agent-signup-email"
                value={formData.email ?? ''}
                error={Boolean(fieldErrors.email)}
                aria-invalid={fieldErrors.email || undefined}
                onChange={(event) => handleEmailChange(event.currentTarget.value)}
              />
            </Field>
            <Field
              label="Phone number"
              id="phone"
              required={false}
              error={fieldErrors.phone ? 'Please enter a valid phone number' : undefined}
            >
              <Input
                id="phone"
                type="tel"
                size={inputSize}
                analyticsName="key-agent-signup-phone"
                value={formData.phoneNumber ?? ''}
                error={Boolean(fieldErrors.phone)}
                aria-invalid={fieldErrors.phone || undefined}
                onChange={(event) => handlePhoneChange(event.currentTarget.value)}
              />
            </Field>
            <Field
              label="Brokerage name"
              id="brokerage"
              required={false}
              description="Share which brokerage you hang your license with"
            >
              <Input
                id="brokerage"
                size={inputSize}
                analyticsName="key-agent-signup-brokerage"
                value={formData.brokerage}
                onChange={(event) => handleInputChange('brokerage', event.currentTarget.value)}
              />
            </Field>
            <Field label="MLS ID" id="mlsId" required={false}>
              <Input
                id="mlsId"
                size={inputSize}
                analyticsName="key-agent-signup-mls-id"
                value={formData.mlsId}
                onChange={(event) => handleInputChange('mlsId', event.currentTarget.value)}
              />
            </Field>
            <Field label="MLS Name" id="mlsName" required={false}>
              <Input
                id="mlsName"
                size={inputSize}
                analyticsName="key-agent-signup-mls-name"
                value={formData.mlsName}
                onChange={(event) => handleInputChange('mlsName', event.currentTarget.value)}
              />
            </Field>
            <Field
              label="What about the Opendoor Key Agent™ program interests you?"
              labelMode="heading"
              required={false}
              description="Select all that apply"
            >
              <Col
                role="group"
                aria-label="What about the Opendoor Key Agent™ program interests you?"
                className="gap-4"
              >
                {INTEREST_OPTIONS.map((option) => (
                  <Checkbox
                    key={option.value}
                    caption={option.label}
                    value={option.value}
                    checked={formData.interests.includes(option.value)}
                    analyticsName="key-agent-signup-checkbox-group"
                    className="shrink-0"
                    onCheckedChange={(checked) => toggleInterest(option.value, checked === true)}
                  />
                ))}
              </Col>
            </Field>
            <Checkbox
              size="sm"
              caption={MARKETING_SMS_CONSENT.checkboxLabel}
              value="marketing-consent"
              analyticsName="key-agent-signup-consent"
              checked={formData.agreeToMarketing}
              className="shrink-0"
              onCheckedChange={(checked) => handleInputChange('agreeToMarketing', checked === true)}
            />
            <Text as="span" className="text-body-sm text-od-content-secondary">
              <MARKETING_SMS_CONSENT.CtaAgreementLinked
                ctaText="Sign up"
                tosAnalyticsName="terms-of-service-key-agent-signup"
                privacyAnalyticsName="privacy-policy-key-agent-signup"
                tosUrl="/terms"
                privacyUrl="/privacy"
                linkProps={{ size: 'small', variant: 'primary' }}
              />
            </Text>
            <Button
              type="submit"
              variant="primary"
              size="lg"
              aria-label="Sign up"
              analyticsName="key-agent-signup-submit"
              loading={isSubmitting}
              disabled={isSubmitting}
              className="self-end max-md:w-full"
            >
              Sign up
            </Button>
            <FormLayout.LegalCopy
              className="pb-6"
              links={[
                {
                  text: 'Do Not Sell My Info',
                  analyticsName: 'key-agent-do-not-sell-my-info',
                  href: FooterRoutes.PRIVACY_REQUEST,
                },
                {
                  text: 'Notice at collection',
                  analyticsName: 'key-agent-notice-at-collection',
                  href: FooterRoutes.CCPA,
                },
                {
                  text: 'Terms of Service',
                  analyticsName: 'key-agent-terms-of-service',
                  href: FooterRoutes.TERMS_OF_SERVICE,
                },
                {
                  text: 'Privacy Policy',
                  analyticsName: 'key-agent-privacy-policy',
                  href: FooterRoutes.PRIVACY_POLICY,
                },
              ]}
            />
          </FormLayout.QuestionContainer>
        </FormLayout.FormContent>
      </FormLayout.ScrollContainer>
    </FormLayout>
  );
};

const KeyAgentSignUp: React.FC = () => (
  <Toasty>
    <KeyAgentSignUpForm />
  </Toasty>
);

export default KeyAgentSignUp;
